import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Home, MessageSquare, Phone, ShoppingBag, CalendarClock, Share2,
  GraduationCap, Plug, Activity, Settings, HelpCircle, Bell, Menu, X, LogOut,
} from "lucide-react";
import { Logo, StatusBadge } from "@/components/brand";
import { LanguageToggle } from "@/components/site/SiteHeader";
import { useLang } from "@/lib/language";
import { toast } from "sonner";

const NAV = [
  { label: "Home", icon: Home, to: "/dashboard", testid: "dash-nav-home" },
  { label: "Tasks", icon: CalendarClock, to: "/dashboard?section=tasks", testid: "dash-nav-tasks" },
  { label: "Conversations", icon: MessageSquare, to: "/inbox", testid: "dash-nav-conversations" },
  { label: "AI Training", icon: GraduationCap, to: "/dashboard?section=training", testid: "dash-nav-ai-training" },
  { label: "Connections", icon: Plug, to: "/dashboard?section=connections", testid: "dash-nav-connections" },
  { label: "Test AI", icon: Activity, to: "/dashboard?section=test", testid: "dash-nav-test" },
  { label: "Activity & Errors", icon: Activity, to: "/dashboard?section=activity", testid: "dash-nav-activity" },
  { label: "Settings", icon: Settings, to: "/dashboard?section=settings", testid: "dash-nav-settings" },
  { label: "Help", icon: HelpCircle, to: "/dashboard?section=help", testid: "dash-nav-help" },
];

export default function DashLayout({ children, title, subtitle, businessName = "Your Business" }) {
  const [aiStatus, setAiStatus] = useState("Active");
  const [menuOpen, setMenuOpen] = useState(false);
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const cycleStatus = () => {
    const order = ["Active", "Paused", "Needs Attention"];
    const next = order[(order.indexOf(aiStatus) + 1) % order.length];
    setAiStatus(next);
    toast.info(`Design preview: AI status set to "${next}"`);
  };

  const logout = async () => {
    try {
      await fetch("/wp-json/csai/v1/logout", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
    } finally {
      localStorage.removeItem("csai_user");
      window.location.href = "/login";
    }
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="border-b border-slate-100 px-5 py-5">
        <Logo size="sm" />
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4 scrollbar-thin" data-testid="dashboard-left-nav">
        {NAV.map((item) => {
          const active = item.to && (location.pathname + location.search) === item.to;
          return (
            <button
              key={item.testid}
              data-testid={item.testid}
              onClick={() => {
                if (item.to) navigate(item.to);
                else toast.info("Design preview: this section opens here in the live product");
                setMenuOpen(false);
              }}
              className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
                active
                  ? "bg-orange-50 text-orange-800"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              <item.icon className={`h-[18px] w-[18px] ${active ? "text-orange-600" : "text-slate-400"}`} />
              {item.label}
            </button>
          );
        })}
      </nav>
      <div className="space-y-1 border-t border-slate-100 p-4">
        <Link
          to="/"
          data-testid="dash-back-to-website-link"
          className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800"
        >
          <Home className="h-[18px] w-[18px] text-slate-400" /> Back to website
        </Link>
        <button
          type="button"
          data-testid="dash-logout-button"
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800"
        >
          <LogOut className="h-[18px] w-[18px] text-slate-400" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#FAFAF7]" data-testid="dashboard-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-slate-100 bg-white lg:block">
        {sidebar}
      </aside>

      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="dashboard-mobile-nav">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">{sidebar}</div>
        </div>
      )}

      <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 border-b border-slate-100 bg-white/90 backdrop-blur-lg" data-testid="dashboard-topbar">
          <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                data-testid="dashboard-mobile-menu-button"
                onClick={() => setMenuOpen(true)}
                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
                aria-label="Open menu"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <div>
                <p className="font-display text-sm font-bold text-slate-900" data-testid="dashboard-business-name">
                  {businessName}
                </p>
                <p className="text-xs text-slate-500">{title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-xs text-slate-500">Automation health not verified</span>
              <span className="hidden sm:block"><LanguageToggle lang={lang} setLang={setLang} compact /></span>
              <button
                data-testid="notifications-button"
                onClick={() => navigate("/dashboard?section=activity")}
                className="relative rounded-full p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5" />
                
              </button>
              <button
                data-testid="user-profile-button"
                onClick={() => navigate("/dashboard?section=settings")}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 font-display text-sm font-bold text-white"
                aria-label="User profile"
              >
                S
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          {subtitle && <p className="mb-6 text-sm text-slate-500">{subtitle}</p>}
          {children}
        </main>
      </div>
    </div>
  );
}
